import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Subscription, catchError, forkJoin, map, of } from 'rxjs';
import {
  NoodleBriefQuestionGroupDto,
  NoodleQuestionDto,
} from '@/app/common/general-types/noodle.dto';
import { NoodleService } from '@/app/services/noodle/noodle.service';

interface ContextDraft {
  picks: readonly string[];
  text: string;
}

/**
 * Steps through generalised rules so each one can receive enrichment context.
 * Options come from the same enrichment call brief enhancements uses.
 * Nothing is posted until Save context.
 */
@Component({
  selector: 'app-review-questions-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './review-questions-drawer.html',
  styleUrl: './review-questions-drawer.scss',
})
export class ReviewQuestionsDrawer implements OnDestroy {
  private readonly noodle = inject(NoodleService);
  private readonly subscriptionList = new Subscription();
  private readonly dialog = viewChild<ElementRef<HTMLDialogElement>>('reviewDrawer');
  private loadGeneration = 0;

  readonly briefTitle = input.required<string>();
  readonly questions = input.required<readonly NoodleQuestionDto[]>();
  readonly questionGroup = input<NoodleBriefQuestionGroupDto | undefined>(undefined);

  readonly saved = output<readonly NoodleQuestionDto[]>();

  readonly stepIndex = signal(0);
  readonly loadState = signal<'idle' | 'loading' | 'ready' | 'error'>('idle');
  readonly saveState = signal<'idle' | 'saving' | 'error'>('idle');
  readonly optionsByQuestion = signal<ReadonlyMap<string, readonly string[]>>(new Map());
  readonly draft = signal<ReadonlyMap<string, ContextDraft>>(new Map());

  readonly steps = computed(() => this.questions().filter((question) => question.generalised));

  readonly current = computed(() => this.steps()[this.stepIndex()]);

  readonly currentOptions = computed(() => {
    const question = this.current();
    return question ? (this.optionsByQuestion().get(question.id) ?? []) : [];
  });

  readonly currentDraft = computed<ContextDraft>(() => {
    const question = this.current();
    if (!question) {
      return { picks: [], text: '' };
    }
    return this.draft().get(question.id) ?? { picks: [], text: '' };
  });

  readonly isLast = computed(() => {
    const count = this.steps().length;
    return count > 0 && this.stepIndex() === count - 1;
  });

  readonly canAdvance = computed(() => this.loadState() === 'ready' && this.saveState() !== 'saving');

  open(): void {
    const steps = this.steps();
    if (steps.length === 0) {
      return;
    }
    this.prepareDraft(steps);
    this.loadOptions(steps);
    const dialog = this.dialog()?.nativeElement;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
  }

  close(): void {
    this.dialog()?.nativeElement?.close();
  }

  onBackdrop(event: Event): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  goTo(index: number): void {
    if (index < 0 || index >= this.steps().length || !this.canAdvance()) {
      return;
    }
    this.stepIndex.set(index);
  }

  back(): void {
    this.goTo(this.stepIndex() - 1);
  }

  next(): void {
    if (!this.canAdvance()) {
      return;
    }
    if (this.isLast()) {
      this.save();
      return;
    }
    this.stepIndex.update((index) => index + 1);
  }

  togglePick(text: string): void {
    const question = this.current();
    if (!question) {
      return;
    }
    this.draft.update((current) => {
      const next = new Map(current);
      const entry = next.get(question.id) ?? { picks: [], text: '' };
      const picks = entry.picks.includes(text)
        ? entry.picks.filter((pick) => pick !== text)
        : [...entry.picks, text];
      next.set(question.id, { ...entry, picks });
      return next;
    });
  }

  isPicked(text: string): boolean {
    return this.currentDraft().picks.includes(text);
  }

  onTextInput(event: Event): void {
    const question = this.current();
    const target = event.target;
    if (!question || !(target instanceof HTMLTextAreaElement)) {
      return;
    }
    const text = target.value;
    this.draft.update((current) => {
      const next = new Map(current);
      const entry = next.get(question.id) ?? { picks: [], text: '' };
      next.set(question.id, { ...entry, text });
      return next;
    });
  }

  dotClass(index: number): string {
    if (index === this.stepIndex()) {
      return 'size-2.5 rounded-full bg-fuchsia-500 ring-2 ring-fuchsia-500/30';
    }
    if (index < this.stepIndex()) {
      return 'size-2.5 rounded-full bg-green-700';
    }
    return 'size-2.5 rounded-full bg-gray-200 dark:bg-white/20';
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  private prepareDraft(steps: readonly NoodleQuestionDto[]): void {
    const draft = new Map<string, ContextDraft>();
    for (const question of steps) {
      const savedTexts = (question.enrichments ?? [])
        .map((item) => item.text.trim())
        .filter((text) => text.length > 0);
      draft.set(question.id, { picks: savedTexts, text: savedTexts[0] ?? '' });
    }
    const firstOpen = steps.findIndex((question) => savedTextsOf(question).length === 0);
    this.stepIndex.set(firstOpen < 0 ? 0 : firstOpen);
    this.draft.set(draft);
    this.saveState.set('idle');
  }

  private loadOptions(steps: readonly NoodleQuestionDto[]): void {
    const generation = ++this.loadGeneration;
    this.loadState.set('loading');
    this.subscriptionList.add(
      forkJoin(
        steps.map((question) => {
          if (!question.generalisedId) {
            return of({ id: question.id, options: [] as string[] });
          }
          return this.noodle.getQuestionEnrichment(question.generalisedId).pipe(
            map((response) => ({ id: question.id, options: enrichmentOptionTexts(response) })),
            catchError(() => of({ id: question.id, options: [] as string[] })),
          );
        }),
      ).subscribe({
        next: (rows) => {
          if (generation !== this.loadGeneration) {
            return;
          }
          this.optionsByQuestion.set(new Map(rows.map((row) => [row.id, row.options])));
          this.draft.update((current) => {
            const next = new Map(current);
            for (const row of rows) {
              if (row.options.length === 0) {
                continue;
              }
              const entry = next.get(row.id);
              if (!entry) {
                continue;
              }
              next.set(row.id, {
                ...entry,
                picks: entry.picks.filter((pick) => row.options.includes(pick)),
              });
            }
            return next;
          });
          this.loadState.set('ready');
        },
        error: () => {
          if (generation === this.loadGeneration) {
            this.loadState.set('error');
          }
        },
      }),
    );
  }

  private save(): void {
    const group = this.questionGroup();
    if (!group || this.saveState() === 'saving') {
      this.saveState.set('error');
      return;
    }
    const updated = group.questions.map((question) => this.withDraftEnrichments(question));
    const packet: NoodleBriefQuestionGroupDto = {
      ...group,
      questions: updated,
      questionsCount: updated.length,
    };
    this.saveState.set('saving');
    this.subscriptionList.add(
      this.noodle.saveQuestionEnrichments([packet]).subscribe({
        next: () => {
          this.saveState.set('idle');
          this.saved.emit(updated);
          this.close();
        },
        error: () => this.saveState.set('error'),
      }),
    );
  }

  private withDraftEnrichments(question: NoodleQuestionDto): NoodleQuestionDto {
    const entry = this.draft().get(question.id);
    if (!entry || !question.generalised) {
      return question;
    }
    const options = this.optionsByQuestion().get(question.id) ?? [];
    const texts = options.length > 0
      ? [...entry.picks]
      : (entry.text.trim() ? [entry.text.trim()] : []);
    return { ...question, enrichments: texts.map((text) => ({ text })) };
  }
}

function savedTextsOf(question: NoodleQuestionDto): string[] {
  return (question.enrichments ?? [])
    .map((item) => item.text.trim())
    .filter((text) => text.length > 0);
}

function enrichmentOptionTexts(response: unknown): string[] {
  if (!response || typeof response !== 'object' || !('enrichments' in response)) {
    return [];
  }
  const enrichments = (response as { enrichments?: unknown }).enrichments;
  if (!Array.isArray(enrichments)) {
    return [];
  }
  const texts: string[] = [];
  for (const item of enrichments) {
    if (!item || typeof item !== 'object' || !('enrichment_text' in item)) {
      continue;
    }
    const text = (item as { enrichment_text?: unknown }).enrichment_text;
    if (typeof text === 'string' && text.trim()) {
      texts.push(text);
    }
  }
  return texts;
}
